/**
 * Lightweight typeahead input — no extra dependencies.
 *
 * Filters a static `items` list by a free-text query against the fields
 * declared in `searchKeys` and renders matching suggestions in a dropdown.
 * The caller picks what to do on selection via `onSelect`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

export function Autocomplete({
  items,
  value,
  onChange,
  onSelect,
  searchKeys = ["label"],
  renderItem,
  placeholder = "",
  testid,
  inputClassName = "",
  autoFocus = false,
  inputMode,
  onKeyDown,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  const query = (value || "").trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query) return [];
    const seen = new Set();
    const out = [];
    for (const item of items) {
      for (const key of searchKeys) {
        const v = (item[key] ?? "").toString().toLowerCase();
        if (v && v.includes(query) && !seen.has(item)) {
          seen.add(item);
          out.push(item);
          break;
        }
      }
      if (out.length >= 8) break;
    }
    return out;
  }, [items, searchKeys, query]);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (item) => {
    onSelect?.(item);
    setOpen(false);
    setHighlight(0);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) {
      onKeyDown?.(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && suggestions[highlight]) {
      e.preventDefault();
      select(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else {
      onKeyDown?.(e);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange?.(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        data-testid={testid}
        autoFocus={autoFocus}
        inputMode={inputMode}
        className={inputClassName}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid={testid ? `${testid}-suggestions` : undefined}>
          {suggestions.map((item, idx) => (
            <button
              key={item.__key ?? item.id ?? idx}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(item)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                highlight === idx ? "bg-[#EEF2FF] text-[#002FA7]" : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              {renderItem ? renderItem(item) : item[searchKeys[0]]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
